import * as React from 'react';
import { cn } from '../utils/cn';

export interface SliderProps {
  /** Current value */
  value?: number;
  /** Default value */
  defaultValue?: number;
  /** Minimum value */
  min?: number;
  /** Maximum value */
  max?: number;
  /** Step size */
  step?: number;
  /** Change handler */
  onChange?: (value: number) => void;
  /** Disable interaction */
  disabled?: boolean;
  /** Show tooltip (antd compat — not implemented, kept for API compat) */
  tooltipVisible?: boolean;
  /** Additional class names */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
}

export function Slider({
  value: controlledValue,
  defaultValue = 0,
  min = 0,
  max = 100,
  step = 1,
  onChange,
  disabled = false,
  className,
  style,
}: SliderProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const value = controlledValue !== undefined ? controlledValue : internalValue;
  const percent = ((value - min) / (max - min)) * 100;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    setInternalValue(val);
    onChange?.(val);
  };

  return (
    <div className={cn('relative flex items-center', className)} style={style}>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        className={cn(
          'w-full h-1.5 appearance-none rounded-full cursor-pointer',
          'bg-gray-200',
          'disabled:opacity-40 disabled:cursor-not-allowed',
          // Webkit thumb
          '[&::-webkit-slider-thumb]:appearance-none',
          '[&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4',
          '[&::-webkit-slider-thumb]:rounded-full',
          '[&::-webkit-slider-thumb]:bg-accent',
          '[&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-accent',
          '[&::-webkit-slider-thumb]:shadow-sm',
          '[&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:duration-fast',
          '[&::-webkit-slider-thumb]:hover:scale-125',
          // Firefox thumb
          '[&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4',
          '[&::-moz-range-thumb]:rounded-full',
          '[&::-moz-range-thumb]:bg-accent',
          '[&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-accent',
          '[&::-moz-range-thumb]:shadow-sm'
        )}
        style={{
          background: `linear-gradient(to right, var(--color-accent) 0%, var(--color-accent) ${percent}%, var(--color-border) ${percent}%, var(--color-border) 100%)`,
        }}
      />
    </div>
  );
}
